import { Box, Typography, Tooltip, IconButton, Chip, LinearProgress } from '@mui/material';
import { CloudUpload, AddPhotoAlternate, ExpandMore, ExpandLess } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useRef, useState } from 'react';
import type { Photo } from '../../../types/photo';
import { FilledSlot, type SlotUploadState } from './PhotoSlotComponents';

/** Structural category shape — satisfied by TriaxialPhotoCategory and the
 *  Rock BTS per-specimen categories (types/rockBtsPhoto.ts). */
interface Category {
  id: string;
  label: string;
  description: string;
}

interface Props {
  category: Category;
  photos: Photo[];
  uploading: SlotUploadState | null;
  onAddPhoto: (file: File) => Promise<void>;
  onRecrop: (photo: Photo) => void;
  onEditCropped: (photo: Photo) => void;
  onDelete: (photoKey: string) => Promise<void>;
  onUpdateComment: (photoKey: string, comment: string | null) => Promise<void>;
  readOnly?: boolean;
  /** Cap on photos in this category — once reached the "Add photo" tile is
   *  gone (Rock TCS/UCS/UCS-NS pass 1: exactly one Before and one After,
   *  BUG-2026-0137/0138 rework). Default: unlimited (triaxial). */
  maxPhotos?: number;
}

/**
 * FEAT-2026-0006: One section for a triaxial photo category. Renders a
 * 2-column grid of existing photos plus a dashed "Add photo" tile at the
 * end. Photo count is user-driven unless `maxPhotos` caps it.
 */
export const TriaxialPhotoCategorySection = ({
  category, photos, uploading, onAddPhoto, onRecrop, onEditCropped, onDelete, onUpdateComment, readOnly = false, maxPhotos,
}: Props) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const remaining = maxPhotos === undefined ? Infinity : Math.max(0, maxPhotos - photos.length);

  const onFiles = async (fileList: FileList | File[] | null) => {
    if (!fileList) return;
    const files = Array.from(fileList).slice(0, remaining === Infinity ? undefined : remaining);
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      await onAddPhoto(file);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, pb: 0.5,
        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        mb: 1.5,
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {category.label}
        </Typography>
        <Chip
          label={photos.length}
          size="small"
          color={photos.length > 0 ? 'primary' : 'default'}
          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
          {category.description}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={collapsed ? 'Expand' : 'Collapse'}>
          <IconButton size="small" onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {!collapsed && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}>
          {photos.map((photo) => (
            <FilledSlot
              key={photo.PhotoKey}
              photo={photo}
              theme={theme}
              onDelete={readOnly ? undefined : () => onDelete(photo.PhotoKey)}
              onRecrop={readOnly ? undefined : () => onRecrop(photo)}
              onEditCropped={readOnly ? undefined : () => onEditCropped(photo)}
              onUpdateComment={readOnly ? undefined : (c) => onUpdateComment(photo.PhotoKey, c)}
            />
          ))}

          {!readOnly && remaining > 0 && (
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (!uploading) onFiles(e.dataTransfer.files);
              }}
              onClick={() => { if (!uploading) inputRef.current?.click(); }}
              sx={{
                height: 280, borderRadius: 1.5,
                cursor: uploading ? 'default' : 'pointer',
                border: `2px dashed ${dragOver ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.18)}`,
                bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.04) : alpha(theme.palette.text.primary, 0.02),
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', gap: 0.7, p: 1.5, textAlign: 'center',
                '&:hover': uploading ? {} : { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.03) },
              }}
            >
              {uploading ? (
                <Box sx={{ width: '80%' }}>
                  <LinearProgress
                    variant={uploading.progress > 0 ? 'determinate' : 'indeterminate'}
                    value={uploading.progress}
                    sx={{ mb: 0.5, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {uploading.step ? `${uploading.step}...` : `${uploading.progress}%`}
                  </Typography>
                </Box>
              ) : photos.length === 0 ? (
                <>
                  <CloudUpload sx={{ fontSize: 36, color: alpha(theme.palette.text.primary, 0.3) }} />
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Add {category.label.toLowerCase()}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Click to upload or drag &amp; drop
                  </Typography>
                </>
              ) : (
                <>
                  <AddPhotoAlternate sx={{ fontSize: 36, color: alpha(theme.palette.primary.main, 0.55) }} />
                  <Typography variant="body2" fontWeight={600} color="primary">
                    Add another photo
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Photo #{photos.length + 1}
                  </Typography>
                </>
              )}

              <input
                ref={inputRef}
                type="file"
                // REQ-2026-0009: accept HEIC (iPhone default) alongside JPEG/PNG.
                accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
                multiple={remaining > 1}
                hidden
                onChange={(e) => {
                  onFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
